import React from 'react';
import type { SortingCrossBorderOnline24hDetailRow } from '../../../../graphql/types/dashboard/SortingCrossBorderOnline24hDetailTypes';
import { formatUtcToPacific } from '../../../../utils/timezoneFormatter';

/** UTC 字符串转 unix 秒；无效返回 null，用于耗时列计算 */
function toUnix(v: string | null | undefined): number | null {
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}

/** 耗时列（小时，保留 2 位）：(smallParcelOnlineTime - customsDeliveryTime) / 3600；任一为空返回 - */
function computeDurationHours(customs: string | null | undefined, online: string | null | undefined): string {
    const a = toUnix(customs);
    const b = toUnix(online);
    if (a == null || b == null) return '-';
    return ((b - a) / 3600).toFixed(2);
}

export interface CrossBorderColumnsOptions {
    /** 点击运单号打开包裹详情（orderId 非空时） */
    onOpenParcel: (orderId: string) => void;
}

/**
 * 跨境24h上网率下钻明细列：口岸仓 / 客户 / 日期 / 大包号 / 运单号 / 交接时间 / 清关交付时间 / 大包入库时间 / 小包上网时间 / 耗时
 *
 * 四列时间统一走 formatUtcToPacific 以美西展示；耗时用 unix 秒差计算，与时区无关。
 */
export function buildCrossBorderOnline24hDetailColumns(opts: CrossBorderColumnsOptions) {
    const { onOpenParcel } = opts;
    const cols: {
        title: string;
        dataIndex: string;
        key: string;
        render?: (v: unknown, r: SortingCrossBorderOnline24hDetailRow) => React.ReactNode;
    }[] = [];

    cols.push({
        title: '口岸仓',
        dataIndex: 'hubName',
        key: 'hubName',
        render: (v: unknown) => (v as string)?.trim() || '-',
    });

    cols.push(
        {
            title: '客户',
            dataIndex: 'customerName',
            key: 'customerName',
            render: (v: unknown) => (v as string)?.trim() || '-',
        },
        {
            title: '日期',
            dataIndex: 'statDate',
            key: 'statDate',
            render: (v: unknown) => (v as string)?.trim() || '-',
        },
        {
            title: '大包号',
            dataIndex: 'bagNumber',
            key: 'bagNumber',
            render: (v: unknown) => (v as string)?.trim() || '-',
        },
        {
            title: '运单号',
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            render: (v: unknown, r: SortingCrossBorderOnline24hDetailRow) => {
                const tn = (v as string) ?? r.trackingNumber ?? '-';
                const oid = r.orderId;
                if (oid == null || oid === 0) return <span>{tn}</span>;
                return (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onOpenParcel(String(oid));
                        }}
                        style={{ color: '#1890ff' }}
                    >
                        {tn}
                    </a>
                );
            },
        },
        {
            title: '交接时间',
            dataIndex: 'handoverTime',
            key: 'handoverTime',
            render: (v: unknown) => formatUtcToPacific(v as string | null),
        },
        {
            title: '清关交付时间',
            dataIndex: 'customsDeliveryTime',
            key: 'customsDeliveryTime',
            render: (v: unknown) => formatUtcToPacific(v as string | null),
        },
        {
            title: '大包入库时间',
            dataIndex: 'bagArrivalTime',
            key: 'bagArrivalTime',
            render: (v: unknown) => formatUtcToPacific(v as string | null),
        },
        {
            title: '小包上网时间',
            dataIndex: 'smallParcelOnlineTime',
            key: 'smallParcelOnlineTime',
            render: (v: unknown) => formatUtcToPacific(v as string | null),
        },
        {
            title: '耗时(小时)',
            dataIndex: '_durationHours',
            key: '_durationHours',
            render: (_: unknown, r: SortingCrossBorderOnline24hDetailRow) =>
                computeDurationHours(r.customsDeliveryTime, r.smallParcelOnlineTime),
        }
    );

    return cols;
}
